import AutorenewIcon from '@mui/icons-material/Autorenew';
import CodeIcon from '@mui/icons-material/Code';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LaunchIcon from '@mui/icons-material/Launch';
import PrintIcon from '@mui/icons-material/Print';
import { Grid, Menu, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import strings from '../../Globalization';
import ActionIconButton from '../../components/common/ActionIconButton';
import consts from '../../consts';
import { useSetState, useUniqueEffect } from '../../hooks';
import util from '../../util';
import { useBreadcrumbs } from '../Breadcrumbs';
import { useCycleInfo } from './Context';

const breadcrumbActionProps = {
	color: 'primary',
	variant: 'contained',
	disableElevation: true,
	endIcon: <KeyboardArrowDownIcon fontSize="inherit" />,
	startIcon: <PrintIcon fontSize="inherit" />,
	size: 'small'
};
const CycleBreadcrumbs = ({startActions}) => {
	const {
		data,
		combos,
		onOpenXmlModal,
		onPreviewTape,
		onPrintTape,
		onOpenCycleReviewFormDialog,
		onPrintDetails,
		onPreviewDetails,
		canReprocessXml,
		onReprocessXml,
		view
	} = useCycleInfo();
	const { tags } = useSelector(({ auth }) => auth);

	
	const cycleTapeVisible =
		data?.CycleTape?.length > 0 && (!view || ['details', 'cycledetails'].includes(view?.toLowerCase?.()));
	const loadRelease = consts.isNodeServer
		? util.localData.LoadReleaseIdAccess || tags?.LoadReleaseId
		: Number(util.localData.LoadReleaseIdAccess || tags?.LoadReleaseId);
	const access = loadRelease === consts.LoadReleaseType.Parametric || util.checkAccess('CycleApprovalAccess');
	const hasCycleReviewAccess = Boolean(tags?.CycleReviewAccess);
	const dateTimeFormat = util.getDateTimeFormat();
	const [, setBreadcrumbsState] = useBreadcrumbs();
	const [state, setState] = useSetState({
		menuOpen: false,
		menuItems: [],
		menuAnchorEl: null
	});

	const handleToggleMenu =
		({ menuItems, menuOpen }) =>
		(event) => {
			setState({
				menuOpen,
				menuItems,
				menuAnchorEl: event?.currentTarget || null
			});
		};
	const handleCloseMenu = () => {
		setState({
			menuOpen: false,
			menuItems: [],
			menuAnchorEl: null
		});
	};

	const handleAction = (action) => () => {
		setState({
			menuOpen: false,
			menuItems: [],
			menuAnchorEl: null
		});
		if (typeof action === 'function') {
			action();
		}
	};

	const handleOnOpenCycleReviewFormDialog = (statusId) => () => {
		setState({
			menuOpen: false,
			menuItems: [],
			menuAnchorEl: null
		});
		onOpenCycleReviewFormDialog(statusId);
	};

	
	const dataLoaded = !!data && Object.keys(data)?.length > 0

	useUniqueEffect(() => {
		if (dataLoaded) {
			setBreadcrumbsState({
				actions: [
					...(Array.isArray(startActions) ? startActions : []),
					...(!tags?.ReadOnly && access && hasCycleReviewAccess
						? [
								{
									...breadcrumbActionProps,
									text: strings.Action,
									sx: {
										backgroundColor: '#f8ac59',
										'&:hover': {
											backgroundColor: '#f8ac59'
										}
									},
									startIcon: <LaunchIcon />,
									onClick: handleToggleMenu({
										menuItems: Array.isArray(combos?.CycleActionStatus)
											? combos.CycleActionStatus.map((status) => ({
													label: status.DisplayValue,
													value: status.LookupId,
													onClick: handleOnOpenCycleReviewFormDialog(status.LookupId)
											  }))
											: [],
										menuOpen: true
									})
								}
						  ]
						: []),
					{
						...breadcrumbActionProps,
						text: strings.Print,
						title: strings.Print,
						onClick: handleToggleMenu({
							menuItems: [
								...(cycleTapeVisible
									? [{ value: 'PrintTape', label: strings.PrintTape, onClick: handleAction(onPrintTape) }]
									: []),
								{ value: 'PrintDetails', label: strings.PrintDetails, onClick: handleAction(onPrintDetails) }
							],
							menuOpen: true
						})
					},
					{
						...breadcrumbActionProps,
						text: strings.PrintPreview,
						title: strings.PrintPreview,
						onClick: handleToggleMenu({
							menuItems: [
								...(cycleTapeVisible
									? [
											{
												value: 'PrintTape',
												label: strings.PreviewTape || 'Preview Tape',
												onClick: handleAction(onPreviewTape)
											}
									  ]
									: []),
								{
									value: 'PrintDetails',
									label: strings.PreviewDetails || 'Preview Details',
									onClick: handleAction(onPreviewDetails)
								}
							],
							menuOpen: true
						})
					},
					...(canReprocessXml
						? [
								{
									component: ActionIconButton,
									title: strings.ReprocessXml,
									children: <AutorenewIcon fontSize="inherit" />,
									onClick: onReprocessXml,
									color: 'tertiary',
									size: 'small',
									variant: 'contained'
								}
						  ]
						: []),
					...(!consts.roleArrayForViewXML.includes(tags.RoleName) ? [{
						component: ActionIconButton,
						title: strings.ViewXML,
						children: <CodeIcon fontSize="inherit" />,
						onClick: onOpenXmlModal,
						color: 'tertiary',
						size: 'small',
						variant: 'contained'
					}] : [])
				],

				breadcrumbs: [
					{
						text: 'CycleGraphSize'
					}
				],
				subbreadcrumbs: [
					{
						text: strings.CycleList,
						variant: 'body2',
						color: 'textSecondary',
						className:"Cycles",
						fontSize: '0.85rem',
						to: `/cyclelist`
					},
					{
						text: [
							strings.CycleGraph,
							`${
								!data?.EquipmentId || data?.EquipmentId === -1
									? ''
									: ` - ${data?.SerialNumber || ''} - ${
											data?.hasOwnProperty('CycleDateTime')
												? dayjs(util.fixDates(data?.CycleDateTime)).format(dateTimeFormat.datetime)
												: ''
									  }`
							}`
						],
						color: 'textPrimary',
						variant: 'body2',
						fontSize: '0.85rem',
						component: 'span',
						className:"Cycles",
					}
				],
				docTitle: strings.CycleGraph
			});
		} else {
			setBreadcrumbsState({
				actions: [],
				subbreadcrumbs: [],
				breadcrumbs: [
					{
						text: 'CycleLogs'
					}
				],
				docTitle: strings.CycleGraph
			});
		}
	}, [dataLoaded, startActions, cycleTapeVisible]);

	return (
		<Grid container>
			<Grid size={12}>
				<Menu anchorEl={state.menuAnchorEl} open={state.menuOpen} onClose={handleCloseMenu}>
					{state.menuItems?.length > 0 &&
						state.menuItems.map(({ value, label, ...rest }, index) => (
							<MenuItem {...rest} key={`menuItem-${index}`}>
								{label}
							</MenuItem>
						))}
				</Menu>
			</Grid>
		</Grid>
	);
};
export default CycleBreadcrumbs;
